import {
  Asset,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouter,
  useTags,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { ThemeProvider } from '@upcaret/ui/theme-provider';
import { Toaster } from '@upcaret/ui/sonner';
import appCss from '../styles.css?url';
import { ErrorComponent } from '../error-component';
import { AppProviders } from '../app/providers/app-providers';
import NotFoundPage from '../app/pages/not-found/NotFoundPage';
import { JsonLd } from '../components/seo/json-ld';
import { buildNotFoundHead, SITE_URL } from '../utils/seo';
import {
  CENTLY_APP_STORE_URL,
  CENTLY_GOOGLE_PLAY_URL,
} from '../constants/app-links';

const SITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: 'Cently',
      alternateName: ['Cently AI', 'Cently.ai', 'Cently app'],
      url: SITE_URL,
      logo: `${SITE_URL}/mark.png`,
      parentOrganization: { name: 'upCaret', url: 'https://upcaret.com' },
      // Canonical Cently-owned profiles. These tell Google cently.ai is a
      // distinct brand from the "Cently" coupon extension.
      sameAs: [
        CENTLY_APP_STORE_URL,
        CENTLY_GOOGLE_PLAY_URL,
        'https://x.com/centlyai',
        'https://www.facebook.com/centlyapp',
        'https://www.producthunt.com/products/cently-2',
        'https://linkedin.com/company/cently',
      ],
    },
    {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      url: SITE_URL,
      name: 'Cently',
      publisher: { '@id': `${SITE_URL}/#organization` },
    },
    {
      '@type': 'SoftwareApplication',
      '@id': `${SITE_URL}/#software-application`,
      name: 'Cently',
      alternateName: ['Cently AI', 'Cently.ai'],
      description:
        'Cently helps you track spending, understand transactions, and build better money habits on web, iOS, and Android.',
      url: SITE_URL,
      sameAs: [CENTLY_APP_STORE_URL, CENTLY_GOOGLE_PLAY_URL],
      applicationCategory: 'FinanceApplication',
      operatingSystem: 'Web, iOS, Android',
      offers: [
        {
          '@type': 'Offer',
          name: 'Monthly',
          price: '7.99',
          priceCurrency: 'USD',
          description: 'Billed monthly. Includes a 7-day free trial.',
        },
        {
          '@type': 'Offer',
          name: 'Annual',
          price: '79.99',
          priceCurrency: 'USD',
          description: 'Billed annually. Includes a 7-day free trial.',
        },
      ],
      publisher: { '@id': `${SITE_URL}/#organization` },
    },
  ],
};

function AppHeadContent() {
  const tags = useTags();
  const router = useRouter();
  const nonce = router.options.ssr?.nonce;

  const filteredTags = tags.filter((tag) => {
    const href = (tag as any)?.attrs?.href;
    return !(
      tag.tag === 'link' &&
      (tag as any)?.attrs?.rel === 'stylesheet' &&
      typeof href === 'string' &&
      href.startsWith('/@tanstack-start/styles.css')
    );
  });

  return (
    <>
      {filteredTags.map((tag) => (
        <Asset {...tag} key={`tsr-meta-${JSON.stringify(tag)}`} nonce={nonce} />
      ))}
    </>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
  {
    head: ({ match }) => ({
      ...(match.status === 'notFound' ? buildNotFoundHead() : {}),
      links: [{ rel: 'stylesheet', href: appCss }],
    }),
    notFoundComponent: NotFoundPage,
    component: () => {
      return (
        <html lang="en">
          <head>
            <meta charSet="utf-8" />
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1"
            />
            <base href="/" />
            <meta name="color-scheme" content="light dark" />
            <meta
              name="theme-color"
              media="(prefers-color-scheme: light)"
              content="#fafafa"
            />
            <meta
              name="theme-color"
              media="(prefers-color-scheme: dark)"
              content="#0a0a0a"
            />
            <meta name="application-name" content="Cently" />
            <link
              rel="apple-touch-icon"
              sizes="180x180"
              href="/favicons/apple-touch-icon.png"
            />
            <link
              rel="icon"
              type="image/png"
              sizes="32x32"
              href="/favicons/favicon-32x32.png"
            />
            <link
              rel="icon"
              type="image/png"
              sizes="16x16"
              href="/favicons/favicon-16x16.png"
            />
            <link rel="icon" href="/favicon.ico" sizes="any" />
            <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
            <meta name="msapplication-TileColor" content="#ffffff" />
            <JsonLd data={SITE_SCHEMA} />
            <AppHeadContent />
          </head>
          <body>
            <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
              <AppProviders>
                <Outlet />
                <Toaster />
              </AppProviders>
            </ThemeProvider>
            <Scripts />
          </body>
        </html>
      );
    },
    errorComponent: ErrorComponent,
  },
);
